import { Box, Slider, styled, Typography } from "@mui/material";
import classNames from "classnames";
import { FC, ReactNode, useCallback, useMemo } from "react";
import BriaTooltip from "../BriaTooltip/BriaTooltip";
import styles from "./BriaSlider.module.scss";

export interface IBriaSliderProps {
	name?: string;
	value?: number;
	defaultValue?: number;
	onChange?: (event: Event, value: number | number[]) => void;
	min?: number;
	max?: number;
	step?: number | null;
	valueLabelDisplay?: "on" | "auto" | "off";
	valueLabelFormat?: (value: number, index?: number) => ReactNode;
	fieldLabels?: string[];
	marks?: { value: number; label?: string }[];
	showOnlyEdgeMarkLabels?: boolean;
	showMarksPoints?: boolean;
	labelsAlwaysGray?: boolean;
	className?: string;
	disabled?: boolean;
	disabledTooltip?: string;
	// Optional: render slider in log scale while exposing real values
	logScale?: boolean;
	logBase?: number;
}

const StyledSlider = styled(Slider)<{ $showMarksPoints: boolean; $labelsAlwaysGray: boolean }>(
	({ $showMarksPoints, $labelsAlwaysGray }) => ({
		"& .MuiSlider-markLabel": {
			fontWeight: "600",
			color: $labelsAlwaysGray && "rgba(var(--black-color-rgb),0.5)",
		},
		'& .MuiSlider-markLabel[data-index="0"]': {
			transform: "translateX(0%)",
		},
		'& .MuiSlider-markLabel[data-index="1"]': {
			transform: "translateX(-100%)",
		},
		"& .MuiSlider-mark": {
			backgroundColor: $showMarksPoints ? "#5300C9;" : "transparent",
			display: "flex",
		},
	}),
);

const BriaSlider: FC<IBriaSliderProps> = ({
	name,
	value,
	onChange,
	min = 0,
	max = 100,
	step = 10,
	defaultValue = 60,
	valueLabelDisplay = "off",
	valueLabelFormat,
	fieldLabels = [],
	marks,
	showOnlyEdgeMarkLabels = false,
	showMarksPoints = false,
	labelsAlwaysGray = false,
	className,
	disabled = false,
	disabledTooltip,
	logScale = false,
	logBase = 10,
}) => {
	// Helpers for handling logarithmic scale values
	const toInternal = useCallback(
		(v: number) => (logScale ? Math.log(v) / Math.log(logBase) : v),
		[logScale, logBase],
	);
	const fromInternal = useCallback((v: number) => (logScale ? Math.pow(logBase, v) : v), [logScale, logBase]);

	const points = useMemo(() => {
		if (step == null) return [];
		const length = Math.floor((max - min) / step) + 1;
		return Array.from({ length }, (_, index) => ({ value: min + index * step }));
	}, [min, max, step]);

	const handleChange = useCallback(
		(e: Event, newValue: number | number[]) => {
			if (logScale) {
				const real = Array.isArray(newValue)
					? (newValue as number[]).map((v) => fromInternal(v))
					: fromInternal(newValue as number);
				if (onChange) onChange(e, real as any);
			} else {
				if (onChange) onChange(e, newValue);
			}
		},
		[onChange, logScale, fromInternal],
	);

	const computedMarks = useMemo(() => {
		if (!marks || !marks.length) return points;
		if (!showOnlyEdgeMarkLabels) return marks;
		return marks.map((m, i) => ({ value: m.value, label: i === 0 || i === marks.length - 1 ? m.label : "" }));
	}, [marks, points, showOnlyEdgeMarkLabels]);

	const internalMarks = useMemo(
		() => computedMarks.map((m: any) => ({ value: toInternal(m.value), label: m.label })),
		[computedMarks, toInternal],
	);

	const slider = (
		<StyledSlider
			name={name}
			value={logScale ? toInternal((value ?? defaultValue) as number) : (value ?? defaultValue)}
			onChange={handleChange}
			min={logScale ? toInternal(min as number) : min}
			max={logScale ? toInternal(max as number) : max}
			step={step}
			valueLabelDisplay={valueLabelDisplay}
			valueLabelFormat={
				logScale
					? (val: number, idx?: number) =>
							(valueLabelFormat
								? valueLabelFormat(fromInternal(val), idx)
								: (fromInternal(val) as unknown as ReactNode)) as any
					: (valueLabelFormat as any)
			}
			marks={logScale ? internalMarks : computedMarks}
			$showMarksPoints={showMarksPoints}
			$labelsAlwaysGray={labelsAlwaysGray}
			className={className}
			disabled={disabled}
		/>
	);

	return (
		<Box className={classNames(styles.container, className)}>
			{disabled && disabledTooltip ? (
				<BriaTooltip title={disabledTooltip} displayOn="hover">
					{slider}
				</BriaTooltip>
			) : (
				slider
			)}
			{fieldLabels && fieldLabels.length === 2 && (
				<Box className={styles.sliderLabels}>
					<Typography>{fieldLabels[0]}</Typography>
					<Typography>{fieldLabels[1]}</Typography>
				</Box>
			)}
		</Box>
	);
};

export default BriaSlider;
